// Group call roles (same server contract as the mobile app): presence heartbeat, speaking seats, requests to speak.
// Seats are attributed by the backend (/api/calls/token can_publish, /hand, /seat); this bar only mirrors them.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;
const opts = { withCredentials: true };

export default function GroupCallSeats({ info, me, socket, canPublish, isHost, onRole }) {
  const { t: tr } = useTranslation();
  const t = (k, o) => tr(`group_seats.${k}`, o);
  const sid = info.session_id;
  const [people, setPeople] = useState({ list: [], max: 0, host: '' });
  const [hand, setHand] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/api/calls/${sid}/participants`, opts)
      .then(({ data }) => setPeople({ list: data.participants || [], max: data.max_speakers || 0, host: data.host_user_id }))
      .catch(() => {});
  }, [sid]);

  // Presence: every 20 s and when the tab comes back, so a quiet or hidden member is never counted as gone.
  useEffect(() => {
    const beat = () => axios.post(`${API}/api/calls/${sid}/heartbeat`, {}, opts).then(({ data }) => {
      if (!data.ok) axios.post(`${API}/api/calls/${sid}/join`, {}, opts).catch(() => {});
      if (isHost) load();
    }).catch(() => {});
    const id = setInterval(beat, 20000);
    const vis = () => { if (document.visibilityState === 'visible') beat(); };
    document.addEventListener('visibilitychange', vis);
    load();
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', vis); };
  }, [sid, isHost, load]);

  useEffect(() => {
    if (!socket) return undefined;
    const onRoleEv = async (d) => {
      if (d?.session_id !== sid) return;
      if (d.user_id === me?.user_id && d.speaker !== canPublish) {
        try { await onRole(d.speaker); setHand(false); toast.info(t(d.speaker ? 'now_speaker' : 'now_listener')); } catch { /* next heartbeat */ }
      }
      load();
    };
    const onHand = (d) => { if (d?.session_id === sid) load(); };
    socket.on('group_call_role', onRoleEv);
    socket.on('group_call_hand', onHand);
    return () => { socket.off('group_call_role', onRoleEv); socket.off('group_call_hand', onHand); };
  }, [socket, sid, me?.user_id, canPublish, onRole, load]);

  const err = (e) => {
    const d = e?.response?.data?.detail;
    toast.error(d?.code === 'seats_full' ? t('seats_full') : (typeof d === 'string' ? d : t('failed')));
  };
  const raise = () => axios.post(`${API}/api/calls/${sid}/hand`, { raised: !hand }, opts)
    .then(({ data }) => { setHand(data.hand_raised); if (data.hand_raised) toast.success(t('hand_sent')); }).catch(err);
  const seat = (userId, grant) => axios.post(`${API}/api/calls/${sid}/seat`, { user_id: userId, grant }, opts).then(load).catch(err);

  const others = people.list.filter((p) => p.user_id !== people.host)
    .sort((a, b) => (b.hand_raised ? 1 : 0) - (a.hand_raised ? 1 : 0) || (b.speaker ? 1 : 0) - (a.speaker ? 1 : 0));
  const used = others.filter((p) => p.speaker).length;
  const hands = others.filter((p) => p.hand_raised).length;
  const role = t(isHost ? 'role_host' : canPublish ? 'role_speaker' : 'role_listener');

  return (
    <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[10001] flex flex-col items-center gap-2" data-testid="group-seats">
      <div className="flex items-center gap-2 rounded-full bg-black/70 text-white px-3 py-1.5 text-xs font-bold backdrop-blur">
        <span className={`px-2 py-0.5 rounded-full ${canPublish ? 'bg-emerald-600' : 'bg-slate-500'}`} data-testid="group-seats-role">{role}</span>
        {!!people.max && <span data-testid="group-seats-count">{t('seats', { u: used, m: people.max })}</span>}
        {!canPublish && (
          <button type="button" onClick={raise} className={`px-2 py-0.5 rounded-full ${hand ? 'bg-amber-600' : 'bg-white/20'}`} data-testid="group-seats-hand">
            {t(hand ? 'lower' : 'raise')}
          </button>
        )}
        {canPublish && !isHost && (
          <button type="button" onClick={() => seat(me?.user_id, false)} className="px-2 py-0.5 rounded-full bg-white/20" data-testid="group-seats-release">{t('release')}</button>
        )}
        {isHost && (
          <button type="button" onClick={() => setOpen(!open)} className={`px-2 py-0.5 rounded-full ${hands ? 'bg-amber-600' : 'bg-white/20'}`} data-testid="group-seats-people">
            {t('people')}{hands ? ` (${hands})` : ''}
          </button>
        )}
      </div>
      {!canPublish && <p className="text-[11px] text-white bg-black/60 rounded-full px-3 py-1" data-testid="group-seats-listener">{t('listener')}</p>}
      {isHost && open && (
        <div className="w-80 max-h-80 overflow-y-auto rounded-xl bg-white shadow-xl p-3 text-sm" data-testid="group-seats-panel">
          {others.length === 0 && <p className="text-gray-500 text-xs">{t('none')}</p>}
          {others.map((p) => (
            <div key={p.user_id} className="flex items-center gap-2 py-1.5 border-b last:border-0" data-testid={`group-seats-person-${p.user_id}`}>
              <span className="flex-1 truncate font-semibold">{p.name}</span>
              <span className={`text-[10px] ${p.hand_raised ? 'text-amber-700' : 'text-gray-500'}`}>{p.hand_raised ? t('asks') : t(p.speaker ? 'role_speaker' : 'role_listener')}{p.away ? ` · ${t('away')}` : ''}</span>
              {p.speaker
                ? <button type="button" onClick={() => seat(p.user_id, false)} className="text-xs text-red-600 border border-red-600 rounded-full px-2" data-testid={`group-seats-revoke-${p.user_id}`}>{t('revoke')}</button>
                : <button type="button" onClick={() => seat(p.user_id, true)} className="text-xs text-white bg-emerald-600 rounded-full px-2" data-testid={`group-seats-grant-${p.user_id}`}>{t('grant')}</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
